"use client";

import { c as _c } from "react/compiler-runtime";
import { app, authentication } from "@microsoft/teams-js";
import React, { useCallback, useEffect, useState } from "react";
const initializeTeamsApp = () => {
  return new Promise<app.Context>((resolve, reject) => {
    app.initialize().then(() => {
      app.getContext().then(context => {
        if (context.user && context.user.tenant && context.user.tenant.id) {
          resolve(context);
        } else {
          reject(new Error("User context or tenant ID is not available in the Teams environment."));
        }
      }).catch((error: Error) => {
        reject(new Error(`Failed to get Teams context: ${error.message}`));
      });
    }).catch((error: Error) => {
      reject(new Error(`Failed to initialize Teams app: ${error.message}`));
    });
  });
};
export const useMS365Cloud: () => ({
  isLoading: true;
} | {
  error: string;
} | {
  redirectUrl: string;
}) & {
  sourceContext: ApplicationSourceContext;
} = () => {
  const $ = _c(19);
  const {
    isLoading,
    error,
    getRedirectUrl,
    allowed,
    sourceContext
  } = useM365ApplicationState();
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [catchedError, setCatchedError] = useState(undefined);
  let t0;
  let t1;
  if ($[0] !== allowed || $[1] !== error || $[2] !== getRedirectUrl || $[3] !== isLoading) {
    t0 = () => {
      if (!allowed) {
        return;
      }
      if (!isLoading && !error) {
        getRedirectUrl().then(url => {
          setRedirectUrl(url);
        }).catch(e => {
          setRedirectUrl(null);
          setCatchedError(e.message);
        });
      }
    };
    t1 = [isLoading, error, allowed, getRedirectUrl];
    $[0] = allowed;
    $[1] = error;
    $[2] = getRedirectUrl;
    $[3] = isLoading;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  if (!allowed) {
    let t2;
    if ($[6] !== sourceContext) {
      t2 = {
        error: "Not allowed",
        sourceContext
      };
      $[6] = sourceContext;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (isLoading) {
    let t2;
    if ($[8] !== sourceContext) {
      t2 = {
        isLoading: true,
        sourceContext
      };
      $[8] = sourceContext;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  if (error) {
    let t2;
    if ($[10] !== error || $[11] !== sourceContext) {
      t2 = {
        error,
        sourceContext
      };
      $[10] = error;
      $[11] = sourceContext;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  if (catchedError) {
    let t2;
    if ($[13] !== catchedError || $[14] !== sourceContext) {
      t2 = {
        error: catchedError,
        sourceContext
      };
      $[13] = catchedError;
      $[14] = sourceContext;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  let t2;
  if ($[16] !== redirectUrl || $[17] !== sourceContext) {
    t2 = {
      redirectUrl,
      sourceContext
    };
    $[16] = redirectUrl;
    $[17] = sourceContext;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  return t2;
};
type M365ApplicationState = {
  error: string | undefined;
  isLoading: boolean;
  getRedirectUrl: () => Promise<string>;
  allowed: boolean;
  sourceContext: ApplicationSourceContext;
  updateSourceContext: (context: ApplicationSourceContext) => void;
};
const M365ApplicationContext = React.createContext<M365ApplicationState | null>(null);
export const useM365ApplicationState = () => {
  const context = React.useContext(M365ApplicationContext);
  if (!context) {
    throw new Error(`useM365ApplicationState must be used within a ${M365ApplicationProvider.name}`);
  }
  return context;
};
export type ApplicationSourceContext = "ms-sso" | "ms-sso-authentication" | "default";
export const M365ApplicationProvider = props => {
  const $ = _c(16);
  const [context, setContext] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [sourceContext, setSourceContext] = useState(props.sourceContext || "default");
  let t0;
  let t1;
  if ($[0] !== sourceContext) {
    t0 = () => {
      if (sourceContext === "default") {
        setIsLoading(false);
        setError("Not a Microsoft SSO user");
        return;
      }
      initializeTeamsApp().then(ctx => {
        if (!(ctx.user && ctx.user.tenant && ctx.user.tenant.id)) {
          throw new Error("User context or tenant ID is not available in the Teams environment.");
        }
        setContext(ctx);
        setIsLoading(false);
      }).catch(e => {
        setError(e.message);
        setIsLoading(false);
      });
    };
    t1 = [sourceContext];
    $[0] = sourceContext;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== context || $[4] !== error || $[5] !== isLoading) {
    t2 = async () => {
      if (error) {
        throw new Error(error);
      }
      if (isLoading) {
        throw new Error("App is still loading");
      }
      ;
      try {
        const token = await authentication.getAuthToken();
        const tenantId = context.user.tenant.id;
        return `/api/ms-sso/user?token=${token}&tenantId=${tenantId}`;
      } catch (t3) {
        const e_0 = t3;
        const errorMessage = e_0 instanceof Error ? e_0.message : "Unknown error";
        throw new Error(`Failed to get authentication token: ${errorMessage}`);
      }
    };
    $[3] = context;
    $[4] = error;
    $[5] = isLoading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const getRedirectUrl = t2;
  const t3 = sourceContext !== "default";
  let t4;
  if ($[7] !== error || $[8] !== getRedirectUrl || $[9] !== isLoading || $[10] !== sourceContext || $[11] !== t3) {
    t4 = {
      error,
      isLoading,
      getRedirectUrl,
      allowed: t3,
      sourceContext,
      updateSourceContext: setSourceContext
    };
    $[7] = error;
    $[8] = getRedirectUrl;
    $[9] = isLoading;
    $[10] = sourceContext;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== props.children || $[14] !== t4) {
    t5 = <M365ApplicationContext.Provider value={t4}>{props.children}</M365ApplicationContext.Provider>;
    $[13] = props.children;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};